import {Trans} from '@lingui/macro'
import {useState} from 'react'
import styled from 'styled-components'

import {
    Box,
    Button,
    Inline,
    Link,
    Modal,
    ModalClose,
    ModalContent,
    Stack,
    Text,
    tokens,
} from '@pleo-io/telescope'

import type {BillingInterval, Interval} from '@product-web/api-types/billing'
import type {PlanType} from '@product-web/api-types/plan'
import {useTermsDocuments} from '@product-web/country-configuration/features/terms/terms'

import ModalPurchaseTermsContent from './modal-purchase-terms-content'
import PlanBreakdown from './review-plan/plan-breakdown'

interface Props {
    isOpen: boolean
    commitmentInterval: Interval
    planType: PlanType
    onDismiss: () => void
    onConfirm: () => void
    billingInterval: BillingInterval
}

enum ModalState {
    PurchaseTerms = 'PurchaseTerms',
    Default = 'Default',
}

const Title = styled(Text)`
    line-height: ${tokens.lineHeight1};
`

const CancelButton = styled(Button)`
    border-color: ${tokens.shade300};
`

const ConfirmChoosePlanModal = ({
    planType,
    isOpen,
    onDismiss,
    onConfirm,
    commitmentInterval,
    billingInterval,
}: Props) => {
    const [confirmChooseModal, setConfirmChooseModal] = useState<ModalState>(ModalState.Default)
    const {masterServiceAgreement, dataProcessingAgreement, privacyPolicy} = useTermsDocuments()
    const onCloseHandle = () => {
        if (confirmChooseModal === ModalState.Default) {
            return onDismiss()
        }
        if (confirmChooseModal === ModalState.PurchaseTerms) {
            return setConfirmChooseModal(ModalState.Default)
        }

        return null
    }

    const DefaultModalContent = () => {
        return (
            <>
                <ModalContent>
                    <Stack space={24} align="center" paddingTop={10}>
                        <Title variant="3xlarge-accent" align="center" color="shade900">
                            <Trans>Review your plan</Trans>
                        </Title>
                        <Text as="p" color="shade600">
                            <Trans>
                                You're about to change your plan. Subscription payments will be
                                taken from your Pleo Wallet.
                            </Trans>
                        </Text>
                        <PlanBreakdown
                            planType={planType}
                            planInterval={commitmentInterval}
                            billingInterval={billingInterval}
                        />
                        <Box my={8}>
                            <Trans>
                                By continuing you agree to the{' '}
                                <Link
                                    onClick={() => setConfirmChooseModal(ModalState.PurchaseTerms)}
                                >
                                    Purchase Terms
                                </Link>
                                ,{' '}
                                <a
                                    href={masterServiceAgreement}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Master Service Agreement
                                </a>
                                ,{' '}
                                <a
                                    href={dataProcessingAgreement}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Data Processing Agreement
                                </a>{' '}
                                and{' '}
                                <a href={privacyPolicy} target="_blank" rel="noopener noreferrer">
                                    Privacy Policy
                                </a>
                            </Trans>
                        </Box>
                        <Inline space={20}>
                            <CancelButton variant="secondary" onClick={() => onDismiss()}>
                                <Trans>Cancel</Trans>
                            </CancelButton>
                            <Button variant="primary" onClick={() => onConfirm()}>
                                <Trans>Agree & Upgrade</Trans>
                            </Button>
                        </Inline>
                    </Stack>
                </ModalContent>
            </>
        )
    }

    return (
        <Modal aria-label="modal" isOpen={isOpen} data-testid="choose-confirm-plan-modal">
            <ModalClose onClick={() => onCloseHandle()} />
            {confirmChooseModal === ModalState.Default ? (
                <>{DefaultModalContent()}</>
            ) : (
                <ModalPurchaseTermsContent onCloseHandle={onCloseHandle} />
            )}
        </Modal>
    )
}

export default ConfirmChoosePlanModal
